import { useCallback, useEffect, useRef, useState } from 'react';

import type { DebounceSettings, DebouncedFunc } from 'lodash';

import debounce from 'lodash/debounce';

// shamelessly copied from https://github.com/gnbaron/use-lodash-debounce/blob/master/src/use-debounce.ts
// but it had an issue with Cancelable https://github.com/DefinitelyTyped/DefinitelyTyped/issues/47166

export function createCallBack<T extends (...args: any[]) => any>(
  callback: T,
  delay: number = 0,
  options?: DebounceSettings
) {
  return debounce(callback, delay, options);
}

export function useDebouncedCallbackRef<T extends (...args: any[]) => any>(
  callback: T,
  delay: number = 0,
  options?: DebounceSettings
) {
  const ref = useRef(useDebouncedCallback(callback, delay, options));

  return ref;
}

export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number = 0,
  options?: DebounceSettings
): DebouncedFunc<T> {
  return useCallback(debounce(callback, delay, options), [callback, delay, options]);
}

export function useDebounce<T>(value: T, delay: number = 0, options?: DebounceSettings): T {
  const previousValue = useRef(value);
  const [current, setCurrent] = useState(value);
  const debouncedCallback = useDebouncedCallback((value: T) => setCurrent(value), delay, options);
  useEffect(() => {
    // does trigger the debounce timer initially
    if (value !== previousValue.current) {
      debouncedCallback(value);
      previousValue.current = value;
      // cancel the debounced callback on clean up
      return debouncedCallback.cancel;
    }
  }, [value]);

  return current;
}
